import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { PlusCircle } from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { ChevronUp, RotateCcw } from "feather-icons-react/build/IconComponents";
import { setToogleHeader } from "../../../core/redux/action";
import SettingsSideBar from "../settingssidebar";
import Table from "../../../core/pagination/datatable";
import axios from "axios";
import Swal from "sweetalert2";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  getEmailTemplates,
  deleteEmailTemplate,
  updateEmailTemplate,
  createEmailTemplate,
} from "../../../service/ApiServices";

const EmailTemplates = () => {
  const dispatch = useDispatch();
  const data = useSelector((state) => state.toggle_header);
  const [templateName, setTemplateName] = useState("");
  const [templateSubject, setTemplateSubject] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [templates, setTemplates] = useState([]);
  const [editTemplate, setEditTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [placeholders, setPlaceholders] = useState([]);
  const [newPlaceholder, setNewPlaceholder] = useState({ name: "" });
  const [showPlaceholderModal, setShowPlaceholderModal] = useState(false);
  const quillRef = useRef(null);

  useEffect(() => {
    fetchTemplates();
    fetchEmailPlaceholders();
  }, []);

  const fetchEmailPlaceholders = async () => {
    try {
      const response = await axios.get("/emailplaceholders");
      
      if (response.data.data.emailPlaceholders) {
        setPlaceholders(response.data.data.emailPlaceholders);
      } else {
        setPlaceholders([]);
      }
    } catch (error) {
      console.error("Error fetching Email placeholders:", error);
      setPlaceholders([]);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch Email Placeholders",
      });
    }
  };

  const handleAddEmailPlaceholder = async () => {
    try {
      if (!newPlaceholder.name.trim()) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Please enter Email placeholder name",
        });
        return;
      }

      const response = await axios.post("/emailplaceholder", {
        name: newPlaceholder.name.trim().toUpperCase(),
      });

      if (response.data) {
        setNewPlaceholder({ name: "" });
        fetchEmailPlaceholders();
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Email Placeholder added successfully",
        });
      }
    } catch (error) {
      console.error("Error adding Email placeholder:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.response?.data?.message || "Failed to add Email placeholder",
      });
    }
  };

  const handleDeleteEmailPlaceholder = async (id) => {
    try {
      await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to Delete this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      }).then(async (result) => {
        if (result.isConfirmed) {
          await axios.delete(`/emailplaceholders/${id}`);
          fetchEmailPlaceholders();
          Swal.fire(
            "Deleted!",
            "Email Placeholder has been deleted.",
            "success"
          );
        }
      });
    } catch (error) {
      console.error("Error deleting Email placeholder:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.response?.data?.message || "Failed to delete Email placeholder",
      });
    }
  };

  const insertEmailPlaceholder = (placeholder, field) => {
    if (field === 'subject') {
      const cursorPosition = document.getElementById('templateSubject').selectionStart;
      const textBefore = templateSubject.substring(0, cursorPosition);
      const textAfter = templateSubject.substring(cursorPosition);
      const newText = textBefore + `{{${placeholder.name}}}` + textAfter;
      setTemplateSubject(newText);
    } else {
      // For description 
      const editor = quillRef.current.getEditor();
      const range = editor.getSelection();
      const placeholderText = `{{${placeholder.name}}}`;
      
      if (range) {
        editor.insertText(range.index, placeholderText);
      } else {
        editor.insertText(editor.getLength(), placeholderText);
      }
    }
  };

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await getEmailTemplates();
      
      if ( response.data.EmailTemplates) {
        const transformedData = response.data.EmailTemplates.map(
          (template) => ({
            key: template._id,
            templateName: template.templateName,
            templateSubject: template.templateSubject,
            templateDescription: template.templateDescription,
            placeholders: template.placeholders,
          })
        );
        setTemplates(transformedData);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch Email templates",
      });
    } finally {
      setLoading(false);
    }
  };
  
  //  extract placeholders from template description
  const extractPlaceholders = (text) => {
    const regex = /{{([^}]+)}}/g;
    const matches = text.match(regex) || [];
    return matches.map((match) => match.replace(/[{}]/g, ""));
  };

  //  find placeholder IDs from names
  const getPlaceholderIds = (placeholderNames) => {
    const result = [];
    
    
    placeholderNames.forEach(name => {
      
      const matchingIds = placeholders
        .filter(p => p.name === name)
        .map(p => p._id);
      
      
      result.push(...matchingIds);
    });
    
    return result;
  };

  const handleEmailTemplate = async (e) => {
    e.preventDefault();
    try {
      // Extract used placeholders from template description
      const usedPlaceholderNames = extractPlaceholders(templateDescription);
      const placeholderIds = getPlaceholderIds(usedPlaceholderNames);
      
      const response = await createEmailTemplate({
        templateName,
        templateSubject,
        templateDescription,
        placeholders: placeholderIds, 
      });

      if (response.data) {
        setTemplateName("");
        setTemplateSubject("");
        setTemplateDescription("");
        fetchTemplates();
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Email Template created successfully.",
        });
      }
    } catch (error) {
      console.error("Error creating template:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.response?.data?.message || "Failed to create Email template",
      });
    }
  };

  const handleEdit = async (template) => {
    setEditTemplate(template);
    setTemplateName(template.templateName);
    setTemplateSubject(template.templateSubject);
    setTemplateDescription(template.templateDescription);
  };
  
  const handleUpdate = async () => {
    try {
      // Extract used placeholders from template description
      const usedPlaceholderNames = extractPlaceholders(templateDescription);
      const placeholderIds = getPlaceholderIds(usedPlaceholderNames);

      await updateEmailTemplate(editTemplate.key, {
        templateName,
        templateSubject,
        templateDescription,
        placeholders: placeholderIds, 
      });

      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Email Template updated successfully",
      });

      setEditTemplate(null);
      setTemplateName("");
      setTemplateSubject("");
      setTemplateDescription("");
      fetchTemplates();
    } catch (error) {
      console.error("Error updating template:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.response?.data?.message || "Failed to update Email template",
      });
    }
  };

  const handleDelete = async (id) => {
    try {
      await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      }).then(async (result) => {
        if (result.isConfirmed) {
          await deleteEmailTemplate(id);
          fetchTemplates();
          Swal.fire("Deleted!", "Email Template has been deleted.", "success");
        }
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to delete Email template",
      });
    }
  };

  const columns = [
    {
      title: "Template Name",
      dataIndex: "templateName",
      width: 700,
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_, record) => (
        <div className="edit-delete-action">
          <Link
            className="me-2 p-2"
            to="#"
            onClick={() => {
              Swal.fire({
                title: "Template Details",
                html: `
                    <div style="text-align: left">
                      <p><strong>Name:</strong> ${record.templateName}</p>
                      <p><strong>Subject:</strong> ${record.templateSubject || 'N/A'}</p>
                      <p><strong>Description:</strong> ${record.templateDescription}</p>
                    </div>
                  `,
                confirmButtonText: "Close",
              });
            }}
          >
            <i className="feather-eye"></i>
          </Link>
          <Link
            className="me-2 p-2"
            to="#"
            data-bs-toggle="modal"
            data-bs-target="#edit-template"
            onClick={() => handleEdit(record)}
          >
            <i className="feather-edit"></i>
          </Link>
          <Link className="p-2" to="#" onClick={() => handleDelete(record.key)}>
            <i className="feather-trash-2"></i>
          </Link>
        </div>
      ),
    },
  ];

  const renderEmailPlaceholders = (field) => {
    if (!Array.isArray(placeholders)) {
      return null;
    }

    return placeholders.map((placeholder) => (
      <button
        key={placeholder._id}
        type="button"
        className="btn btn-outline-primary me-2 mb-2"
        onClick={() => insertEmailPlaceholder(placeholder, field)}
        title={placeholder.description}
      >
        {placeholder.name}
      </button>
    ));
  };

  const renderRefreshTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Refresh
    </Tooltip>
  );

  const renderCollapseTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Collapse
    </Tooltip>
  );

  
  const quillStyles = {
    height: '400px',
    marginBottom: '50px'
  };

  return (
    <div>
      <div className="page-wrapper">
        <div className="content settings-content">
          <div className="page-header settings-pg-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>Settings</h4>
                <h6>Manage your settings on portal</h6>
              </div>
            </div>
            <ul className="table-top-head">
              <li>
                <OverlayTrigger placement="top" overlay={renderRefreshTooltip}>
                  <Link 
                    data-bs-toggle="tooltip" 
                    data-bs-placement="top"
                    onClick={fetchTemplates}
                  >
                    <RotateCcw />
                  </Link>
                </OverlayTrigger>
              </li>
              <li>
                <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
                  <Link
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    id="collapse-header"
                    className={data ? "active" : ""}
                    onClick={() => {
                      dispatch(setToogleHeader(!data));
                    }}
                  >
                    <ChevronUp />
                  </Link>
                </OverlayTrigger>
              </li>
            </ul>
          </div>
          <div className="row">
            <div className="col-xl-12">
              <div className="settings-wrapper d-flex">
                <SettingsSideBar />
                <div className="settings-page-wrap">
                  <div className="setting-title">
                    <h4>Email Templates</h4>
                  </div>

                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex justify-content-between mb-3">
                        <button
                          className="btn btn-primary"
                          data-bs-toggle="modal"
                          data-bs-target="#add-template"
                        >
                          <PlusCircle className="me-2" />
                          Add New Email Template
                        </button>
                        <button 
                          className="btn btn-secondary"
                          onClick={() => setShowPlaceholderModal(true)}
                        >
                          <PlusCircle className="me-2" />
                          Add New Placeholder
                        </button>
                      </div>
                      <div className="table-responsive">
                        <Table 
                          columns={columns}
                          dataSource={templates}
                          loading={loading}
                        />
                      </div>
                      <div className="modal fade" id="add-template" data-bs-backdrop="static" data-bs-keyboard="false">
                        <div className="modal-dialog modal-dialog-centered modal-lg">
                          <div className="modal-content">
                            <div className="modal-header">
                              <h5 className="modal-title">Add Email Template</h5>
                              <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                              ></button>
                            </div>
                            <div className="modal-body">
                              <form>
                                <div className="mb-3">
                                  <label className="form-label">
                                    Template Name
                                  </label>
                                  <input 
                                    type="text" 
                                    className="form-control" 
                                    value={templateName}
                                    onChange={(e) => setTemplateName(e.target.value)}
                                  />
                                </div>
                                <div className="mb-3">
                                  <label className="form-label">Subject</label>
                                  <input 
                                    type="text" 
                                    className="form-control"
                                    id="templateSubject"
                                    value={templateSubject}
                                    onChange={(e) => setTemplateSubject(e.target.value)}
                                  />
                                  <div className="mt-2">
                                    <label className="form-label">Available Placeholders for Subject</label>
                                    <div className="placeholder-buttons">
                                      {renderEmailPlaceholders('subject')}
                                    </div>
                                  </div>
                                </div>
                                <div className="mb-3">
                                  <label className="form-label">Email Body</label>
                                  <div className="position-relative" style={quillStyles}>
                                    <ReactQuill
                                      ref={quillRef}
                                      theme="snow"
                                      value={templateDescription}
                                      onChange={setTemplateDescription}
                                      modules={{
                                        toolbar: [
                                          [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                                          ['bold', 'italic', 'underline', 'strike'],
                                          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                                          [{ 'color': [] }, { 'background': [] }],
                                          ['link', 'image'],
                                          ['clean']
                                        ]
                                      }}
                                      style={{ height: '300px' }}
                                    />
                                  </div>
                                  <div className="mt-2">
                                    <label className="form-label">Available Placeholders for Body</label>
                                    <div className="placeholder-buttons">
                                      {renderEmailPlaceholders('description')}
                                    </div>
                                  </div>
                                </div>
                              </form>
                            </div>
                            <div className="modal-footer">
                              <button
                                type="button"
                                className="btn btn-secondary"
                                data-bs-dismiss="modal"
                              >
                                Close
                              </button>
                              <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleEmailTemplate}
                                data-bs-dismiss="modal"
                              >
                                Save Template
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="modal fade" id="edit-template" data-bs-backdrop="static" data-bs-keyboard="false">
                        <div className="modal-dialog modal-dialog-centered modal-lg">
                          <div className="modal-content">
                            <div className="modal-header">
                              <h5 className="modal-title">Edit Email Template</h5>
                              <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                              ></button>
                            </div>
                            <div className="modal-body">
                              <form>
                                <div className="mb-3">
                                  <label className="form-label">
                                    Template Name
                                  </label>
                                  <input 
                                    type="text" 
                                    className="form-control"
                                    value={templateName}
                                    onChange={(e) => setTemplateName(e.target.value)}
                                  />
                                </div>
                                <div className="mb-3">
                                  <label className="form-label">Subject</label>
                                  <input 
                                    type="text" 
                                    className="form-control"
                                    id="editTemplateSubject"
                                    value={templateSubject}
                                    onChange={(e) => setTemplateSubject(e.target.value)}
                                  />
                                  <div className="mt-2">
                                    <label className="form-label">Available Placeholders for Subject</label>
                                    <div className="placeholder-buttons">
                                      {renderEmailPlaceholders('subject')}
                                    </div>
                                  </div>
                                </div>
                                <div className="mb-3">
                                  <label className="form-label">Email Body</label>
                                  <div className="position-relative" style={quillStyles}>
                                    <ReactQuill
                                      ref={quillRef}
                                      theme="snow"
                                      value={templateDescription}
                                      onChange={setTemplateDescription}
                                      modules={{
                                        toolbar: [
                                          [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                                          ['bold', 'italic', 'underline', 'strike'],
                                          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                                          [{ 'color': [] }, { 'background': [] }],
                                          ['link', 'image'],
                                          ['clean']
                                        ]
                                      }}
                                      style={{ height: '300px' }}
                                    />
                                  </div>
                                  <div className="mt-2">
                                    <label className="form-label">Available Placeholders for Body</label>
                                    <div className="placeholder-buttons">
                                      {renderEmailPlaceholders('description')}
                                    </div>
                                  </div>
                                </div>
                              </form>
                            </div>
                            <div className="modal-footer">
                              <button
                                type="button"
                                className="btn btn-secondary"
                                data-bs-dismiss="modal"
                              >
                                Close
                              </button>
                              <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleUpdate}
                                data-bs-dismiss="modal"
                              >
                                Update Template
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        className={`modal fade ${
                          showPlaceholderModal ? "show" : ""
                        }`}
                        id="add-placeholder"
                        style={{
                          display: showPlaceholderModal ? "block" : "none",
                        }}
                      >
                        <div className="modal-dialog modal-dialog-centered">
                          <div className="modal-content">
                            <div className="modal-header">
                              <h5 className="modal-title">
                                Manage Email Placeholders
                              </h5>
                              <button
                                type="button"
                                className="btn-close"
                                onClick={() => setShowPlaceholderModal(false)}
                              ></button>
                            </div>
                            <div className="modal-body">
                              <form className="mb-4">
                                <div className="input-group">
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={newPlaceholder.name}
                                    onChange={(e) =>
                                      setNewPlaceholder({
                                        name: e.target.value,
                                      })
                                    }
                                    placeholder="Enter placeholder name (e.g., CUSTOMER_EMAIL)"
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleAddEmailPlaceholder}
                                  >
                                    Add
                                  </button>
                                </div>
                              </form>

                              <div className="existing-placeholders">
                                <h6 className="mb-3">Existing Email Placeholders</h6>
                                {Array.isArray(placeholders) &&
                                placeholders.length > 0 ? (
                                  <div className="list-group">
                                    {placeholders.map((placeholder) => (
                                      <div
                                        key={placeholder._id}
                                        className="list-group-item d-flex justify-content-between align-items-center"
                                      >
                                        <span className="placeholder-name">
                                          {placeholder.name}
                                        </span>
                                        <button
                                          className="btn btn-sm btn-danger"
                                          onClick={() =>
                                            handleDeleteEmailPlaceholder(
                                              placeholder._id
                                            )
                                          }
                                        >
                                          <i className="feather-trash-2"></i>
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-muted">
                                    No placeholders added yet
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="modal-footer">
                              <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => setShowPlaceholderModal(false)}
                              >
                                Close
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailTemplates;